import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  whatsappTemplate,
  linkedinTemplate,
  emailTemplate,
} from "../../lib/share-templates";
import OnboardingLayout from "./OnboardingLayout";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  CheckCircle2,
  Copy,
  Download,
  Linkedin,
  Mail,
  MessageCircle,
  ExternalLink,
  Loader2,
  ArrowRight,
  LayoutDashboard,
} from "lucide-react";

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export default function Step5Live() {
  const { campaignId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [linksRes, qrRes] = await Promise.all([
          api.get("/consultants/me/share-links"),
          api.get(`/consultants/me/share-links/${campaignId}/qr`),
        ]);
        if (cancelled) return;
        const c = (linksRes.data || []).find((l) => l.id === campaignId);
        setCampaign(c || null);
        setQr(qrRes.data || null);
      } catch (err) {
        if (!cancelled) toast.error("Couldn't load your campaign — please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const url = campaign?.url || "";

  const onCopy = useCallback(async () => {
    const ok = await copyToClipboard(url);
    ok
      ? toast.success("Link copied to clipboard")
      : toast.error("Couldn't copy — please copy manually.");
  }, [url]);

  const onDownloadQR = useCallback(() => {
    if (!qr?.qr_data_url) return;
    const a = document.createElement("a");
    a.href = qr.qr_data_url;
    a.download = `${(campaign?.name || "growth-campaign")
      .replace(/\s+/g, "-")
      .toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR code downloaded");
  }, [qr, campaign]);

  const onShareLinkedIn = useCallback(async () => {
    const text = linkedinTemplate(user, campaign, url);
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success("LinkedIn post copied — opening LinkedIn…");
      window.open(
        "https://www.linkedin.com/feed/?shareActive=true",
        "_blank",
        "noopener"
      );
    } else {
      toast.error("Couldn't copy — please copy manually.");
    }
  }, [user, campaign, url]);

  const onShareWhatsApp = useCallback(() => {
    const text = whatsappTemplate(user, campaign, url);
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encoded}`, "_blank", "noopener");
  }, [user, campaign, url]);

  const onShareEmail = useCallback(() => {
    const { subject, body } = emailTemplate(user, campaign, url);
    const href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }, [user, campaign, url]);

  if (loading) {
    return (
      <OnboardingLayout currentStep="launch" hideRail>
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </OnboardingLayout>
    );
  }

  if (!campaign) {
    return (
      <OnboardingLayout currentStep="launch">
        <p className="text-sm text-muted-foreground">Campaign not found.</p>
        <Button className="mt-6" onClick={() => nav("/dashboard")}>
          Go to dashboard
        </Button>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep="launch" maxWidth="max-w-3xl">
      <div className="flex flex-col items-start">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs uppercase tracking-widest font-sans-ui"
          data-testid="live-status-badge"
        >
          <CheckCircle2 className="w-4 h-4" /> Campaign live
        </div>
        <h1
          className="mt-6 font-display text-4xl sm:text-5xl leading-tight text-foreground"
          data-testid="live-headline"
        >
          You&apos;re live.
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-xl">
          Share the link with a few leaders now — you can be reviewing your
          first scored lead within 24 hours.
        </p>
      </div>

      {/* Primary card: link + QR */}
      <Card
        className="mt-8 card-elevated p-6 sm:p-8"
        data-testid="live-primary-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="overline text-xs uppercase tracking-widest text-muted-foreground">
              Campaign
            </div>
            <h2
              className="mt-1 font-sans-ui font-semibold text-lg text-foreground truncate"
              data-testid="live-campaign-name"
            >
              {campaign.name}
            </h2>
            <div
              className="mt-3 rounded-md bg-secondary/40 border border-border/60 px-3 py-2 break-all text-xs font-mono text-muted-foreground"
              data-testid="live-campaign-url"
            >
              {url}
            </div>
            <div className="mt-4 flex gap-3 flex-wrap">
              <Button
                onClick={onCopy}
                className="min-w-[7rem]"
                data-testid="live-copy-link"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy link
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(url, "_blank", "noopener")}
                data-testid="live-preview-link"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Preview
              </Button>
            </div>
          </div>
          {qr?.qr_data_url && (
            <div className="flex flex-col items-center gap-2">
              <img
                src={qr.qr_data_url}
                alt="Campaign QR code"
                className="w-32 h-32 rounded-md border border-border/60 bg-white p-2"
                data-testid="live-qr-image"
              />
              <button
                type="button"
                onClick={onDownloadQR}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition"
                data-testid="live-download-qr"
              >
                <Download className="w-3 h-3" /> Download QR
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Share templates — ready to paste */}
      <div className="mt-10">
        <div className="overline text-xs uppercase tracking-widest text-muted-foreground mb-4">
          Share it in one click
        </div>
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3"
          data-testid="live-share-actions"
        >
          <button
            type="button"
            onClick={onShareLinkedIn}
            className="flex items-center gap-3 rounded-lg border border-border/60 hover:border-accent/50 bg-card/60 p-4 text-left transition"
            data-testid="live-share-linkedin"
          >
            <div className="w-10 h-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <Linkedin className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-sans-ui font-semibold text-sm">
                LinkedIn post
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Copies the post, opens LinkedIn.
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={onShareWhatsApp}
            className="flex items-center gap-3 rounded-lg border border-border/60 hover:border-accent/50 bg-card/60 p-4 text-left transition"
            data-testid="live-share-whatsapp"
          >
            <div className="w-10 h-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-sans-ui font-semibold text-sm">
                WhatsApp message
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Opens WhatsApp with a filled draft.
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={onShareEmail}
            className="flex items-center gap-3 rounded-lg border border-border/60 hover:border-accent/50 bg-card/60 p-4 text-left transition"
            data-testid="live-share-email"
          >
            <div className="w-10 h-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-sans-ui font-semibold text-sm">
                Email invitation
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Opens your mail app with the draft.
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* What's next */}
      <div
        className="mt-10 rounded-lg border border-border/60 bg-card/40 p-5 flex items-start gap-4"
        data-testid="live-next-card"
      >
        <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <LayoutDashboard className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-sans-ui font-semibold text-sm text-foreground">
            What happens next
          </div>
          <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
            Every completed diagnostic lands in your dashboard as a scored
            lead with an Executive Growth Report ready to send. You&apos;ll be
            notified by email.
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={() => nav("/dashboard")}
          data-testid="live-go-to-dashboard"
        >
          Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </OnboardingLayout>
  );
}
